import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import casesService from "api/casesService";
import Avatar from "components/Avatar";
import { generateCaseMetricPdf } from "utils/generateCaseMetricPdf";
import SplitSlide, { IPreviewState } from "components/SplitSlide";
import PdfPreviewPane from "components/SplitSlide/PdfPreviewPane";
import styles from "./styles.module.scss";

// Wspólne klucze proporcji/zoomu dla layoutu metryk (model 1 = SplitSlide).
const LS_SPLIT_KEY = "metric_slide_split_v1";
const LS_ZOOM_KEY = "metric_slide_zoom_v1";

interface Props {
  caseItem: ICaseSimple;
  statusName?: string;
  onClose: () => void;
}

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const courtName = (c: ICaseSimple): string => {
  const anyC = c as unknown as { CourtShortcut?: string; Court?: string };
  return anyC.CourtShortcut || anyC.Court || "—";
};

const CaseMetricSlide = ({ caseItem, statusName, onClose }: Props) => {
  const [generating, setGenerating] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  // Historia teczki (timeline jak w popoverze CaseInfo).
  const { data: history } = casesService.useGetCaseHistory(caseItem.Id);
  const participants = caseItem.Participants ?? [];

  const status = statusName || "—";

  // Model 1 (SplitSlide): podgląd po lewej jest ZAWSZE widoczny, więc metrykę
  // generujemy automatycznie po otwarciu (bez przycisku „PDF").
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const blob = await generateCaseMetricPdf({
          title: caseItem.Title ?? "",
          signature: caseItem.Signature ?? "",
          client: caseItem.Client ?? "",
          status,
          leadBy: caseItem.LeadBy ?? "",
          court: courtName(caseItem),
          createdDate: caseItem.CreationTimestamp ?? "",
          participants: participants.map((p) => ({
            name: p.Name || p.Label || "—",
            meta: [p.Email, p.Phone].filter(Boolean).join(" · "),
            isMain: p.IsMain,
          })),
          history: (history ?? []).map((h) => ({
            user: h.UserLabel,
            description: h.Description,
            date: h.Date
              ? new Date(h.Date).toLocaleString("pl-PL", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
          })),
        });
        setPdfBlob(blob);
      } catch {
        setPdfError("Nie udało się przygotować podglądu metryki.");
      } finally {
        setGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewState: IPreviewState | null = generating
    ? { text: "Generowanie podglądu metryki…" }
    : pdfError
    ? { text: pdfError, isError: true }
    : !pdfBlob
    ? { text: "Brak podglądu metryki." }
    : null;

  return (
    <SplitSlide
      title="Metryka teczki / sprawy"
      splitStorageKey={LS_SPLIT_KEY}
      devTodoKey="slide:case-metric"
      onClose={onClose}
      previewState={previewState}
      preview={
        pdfBlob && (
          <PdfPreviewPane
            file={pdfBlob}
            title="Metryka teczki"
            zoomStorageKey={LS_ZOOM_KEY}
          />
        )
      }
    >
      <div className={styles.body}>
        {/* Dane teczki — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("info")}
        >
          <span>Dane teczki</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.info ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.info && (
          <div className={styles.metaCard}>
            <div className={styles.metaTitleRow}>
              <div className={styles.metaTitleLabel}>Tytuł</div>
              <div className={styles.metaTitleValue}>
                {caseItem.Title || "—"}
              </div>
            </div>
            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Sygnatura</span>
                <span className={styles.metaValue}>
                  {caseItem.Signature || "—"}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Status</span>
                <span className={styles.metaValue}>{status}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Wszczęta</span>
                <span className={styles.metaValue}>
                  {fmtDate(caseItem.CreationTimestamp)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Prowadzący</span>
                <span className={styles.metaValue}>
                  {caseItem.LeadBy || "—"}
                </span>
              </div>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Organ prowadzący</span>
                <span className={styles.metaValue}>{courtName(caseItem)}</span>
              </div>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Podmiot</span>
                <span className={styles.metaValue}>
                  {caseItem.Client || "—"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Osoby powiązane ze sprawą — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("persons")}
        >
          <span>Osoby powiązane ze sprawą</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.persons ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.persons &&
          (participants.length === 0 ? (
            <div className={styles.noTimeline}>
              Brak osób powiązanych ze sprawą
            </div>
          ) : (
            <div className={styles.persons}>
              {participants.map((p, i) => {
                const name = p.Name || p.Label || "—";
                const meta = [p.Email, p.Phone].filter(Boolean).join(" · ");
                return (
                  <div key={p.Id ?? i} className={styles.personRow}>
                    <Avatar
                      name={name}
                      size="small"
                      className={styles.personAvatar}
                    />
                    <div className={styles.personText}>
                      <div className={styles.personName}>
                        {name}
                        {p.IsMain && (
                          <span className={styles.personBadge}>główny</span>
                        )}
                      </div>
                      {meta && <div className={styles.personMeta}>{meta}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        {/* Historia teczki — timeline (jak w popoverze CaseInfo) */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("history")}
        >
          <span>Historia teczki</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.history ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.history &&
          (!history || history.length === 0 ? (
            <div className={styles.noTimeline}>Brak historii teczki</div>
          ) : (
            <div className={styles.historyList}>
              {history.map((h, i) => (
                <div key={i} className={styles.historyItem}>
                  <Avatar
                    name={h.UserLabel}
                    size="small"
                    className={styles.historyAvatar}
                  />
                  <div className={styles.historyContent}>
                    <div className={styles.historyUser}>{h.UserLabel}</div>
                    <div className={styles.historyDesc}>{h.Description}</div>
                    <div className={styles.historyDate}>
                      {h.Date
                        ? new Date(h.Date).toLocaleString("pl-PL", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </SplitSlide>
  );
};

export default CaseMetricSlide;
